import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../http-client';
import { useIsAuthenticated } from '../../state_management_and_stores/auth.store';

// --- Query Key Factory ---
//
// IMPORTANT: every key MUST be nested under `registrationOpsKeys.all` so the
// invalidation in `useCancelTeamRegistration` (and the other registration
// mutations below) cascades to all derived data — including the registration
// policy and the user's outstanding balance. A previous version of the
// registration form fetched those two via `useEffect` keyed only on
// `eventId`, which left the modal showing stale "payment pending" / unpaid
// fee state after a cancel → re-register flow until the page was reloaded.
// Keeping them inside this key tree fixes that without ad-hoc refresh logic.
export const registrationOpsKeys = {
  all: ['registration-ops'] as const,
  // Conditional spreads keep invalidateQueries prefix matching working —
  // trailing `undefined` in keys defeats React Query's partialDeepEqual.
  myRegistrations: (filters?: MyRegistrationsParams) =>
    filters
      ? ([...registrationOpsKeys.all, 'my', filters] as const)
      : ([...registrationOpsKeys.all, 'my'] as const),
  eventRegistrations: (eventId: string, params?: PaginationParams) =>
    params
      ? ([...registrationOpsKeys.all, 'event', eventId, params] as const)
      : ([...registrationOpsKeys.all, 'event', eventId] as const),
  eventCapacity: (eventId: string) =>
    [...registrationOpsKeys.all, 'capacity', eventId] as const,
  partnerNotifications: () =>
    [...registrationOpsKeys.all, 'partner-notifications'] as const,
  handicapCheck: (eventId: string, position: string) =>
    [...registrationOpsKeys.all, 'handicap-check', eventId, position] as const,
  registrationPolicy: (eventId: string) =>
    [...registrationOpsKeys.all, 'registration-policy', eventId] as const,
  outstandingBalance: (userId?: string) =>
    userId
      ? ([...registrationOpsKeys.all, 'outstanding-balance', userId] as const)
      : ([...registrationOpsKeys.all, 'outstanding-balance'] as const),
  buddyGroupStatus: (eventId: string, buddyGroupId: string) =>
    [...registrationOpsKeys.all, 'buddy-group-status', eventId, buddyGroupId] as const,
};

// --- Types ---
interface Registration {
  id: string;
  event_id: string;
  user_profile_id: string;
  position: 'Header' | 'Heeler';
  registration_type: 'individual' | 'team_primary' | 'team_partner';
  partner_status:
    | 'pending'
    | 'approved'
    | 'rejected'
    | 'cancelled'
    | 'none'
    | 'needs_partner';
  [key: string]: unknown;
}

interface RegisterData {
  event_id: string;
  position: 'Header' | 'Heeler';
  registration_type: 'individual' | 'team_primary' | 'team_partner';
  partner_email?: string;
  partner_user_profile_id?: string;
  buddy_group_id?: string;
  buddy_group_position?: number;
  payment_method?: string;
  paid_for_partner?: boolean;
  notes?: string;
  manual_handicap_value?: number;
}

export interface HandicapCheckResult {
  needs_handicap: boolean;
  existing_handicap_value: number | null;
  handicap_id: string | null;
  organization_name: string | null;
  handicap_type_name: string | null;
  is_approved: boolean;
  is_user_entered: boolean;
}

interface PartnerRespondData {
  /**
   * `accept` (not `accepted`) — backend schema is
   * PartnerRespondBodySchema = { accept: boolean }. Sending `accepted`
   * triggered a 400 Bad Request on partner approval.
   */
  accept: boolean;
  notes?: string | null;
}

interface MyRegistrationsParams {
  event_id?: string;
  status?: string;
  /**
   * Restrict to registrations whose event belongs to this rodeo. Used by
   * the registration wizard to default new entries to the user's last
   * preference selection across other events in the same rodeo.
   */
  rodeo_id?: string;
  /**
   * Also return active registrations the user can see via buddy-group
   * membership (rows the group owns but the user doesn't). Each row gets
   * `is_buddy_group_member_view: true` plus `buddy_group_name`.
   */
  include_buddy_group?: boolean;
}

interface PaginationParams {
  page?: number;
  pageSize?: number;
}

interface PartnerNotification {
  id: string;
  registration_id: string;
  user_profile_id: string;
  message: string;
  is_read: boolean;
  [key: string]: unknown;
}

interface CapacityInfo {
  total_capacity: number;
  registered_count: number;
  available_spots: number;
  waitlist_count: number;
  /** True only when max_contestants is set AND filled — uncapped events return false. */
  is_full: boolean;
}

/** Response from /registration-payment-ops/register */
export interface PaymentRegistrationResponse {
  registration_id: string;
  event_id: string;
  user_profile_id: string;
  payment_id: string | null;
  payment_deadline: string | null;
  total_amount: number;
  fees_breakdown: {
    entry_fee: number;
    transaction_fee: number;
    total_amount: number;
    currency: string;
  };
  /**
   * Stripe client_secret. Used by the frontend to mount Stripe Elements
   * and confirm payment client-side. Null when payment_method='cash' AND
   * on the deferred-mint path (client_secret arrives from /mint-intent
   * at submit time).
   */
  client_secret: string | null;
  /** Stripe PaymentIntent id (pi_…). */
  stripe_payment_intent_id?: string;
  /** Stripe publishable key for the active env. */
  stripe_publishable_key?: string;
  status: string;
  payment_status: string;
  partner_registration_id: string | null;
  /**
   * Deferred-mint only. Totals quoted for BOTH payment methods so the
   * checkout dialog can show the user the card-vs-ACH price difference
   * without a second backend round-trip. ACH waives the transaction fee.
   */
  totals_by_method?: {
    card: { total_amount: number; transaction_fee_total: number };
    us_bank_account: { total_amount: number; transaction_fee_total: number };
  };
}

/** Response from POST /mint-intent. */
export interface MintIntentResponse {
  client_secret: string;
  stripe_payment_intent_id: string;
  total_amount: number;
  transaction_fee_total: number;
  stripe_publishable_key: string;
}

/** Response from /cash-payment-ops/register */
export interface CashRegistrationResponse {
  registration_id: string;
  status: string;
  message: string;
  partner_registration_id: string | null;
  partner_status: string | null;
}

/** Data for Stripe registration */
interface RegisterWithPaymentData {
  event_id: string;
  position: 'Header' | 'Heeler';
  registration_type?: 'individual' | 'team_primary' | 'team_partner';
  partner_user_profile_id?: string;
  partner_email?: string;
  paid_for_partner?: boolean;
  /**
   * 'stripe' = pay online (card or ACH via Stripe Elements). 'cash' = pay
   * organizer in person.
   */
  payment_method?: 'stripe' | 'cash';
  buddy_group_id?: string;
  buddy_group_position?: number;
  notes?: string;
  manual_handicap_value?: number;
  return_url?: string;
  cancel_url?: string;
}

/** Data for cash registration */
interface RegisterWithCashData {
  event_id: string;
  position: 'Header' | 'Heeler';
  registration_type?: 'individual' | 'team_primary' | 'team_partner';
  partner_user_profile_id?: string;
  partner_email?: string;
  paid_for_partner?: boolean;
  buddy_group_id?: string;
  buddy_group_position?: number;
  notes?: string;
  manual_handicap_value?: number;
}

/** Data for completing a Stripe payment after Elements confirmation */
interface CompletePaymentData {
  registrationId: string;
  /** Stripe PaymentIntent id (pi_…). */
  payment_intent_id: string;
}

// --- Query Hooks ---
export function useMyRegistrations(
  filters?: MyRegistrationsParams,
  options?: { enabled?: boolean },
) {
  // "My" registrations only make sense for signed-in users. Skip the query
  // entirely for visitors so the public-browse pages don't 401.
  const isAuthenticated = useIsAuthenticated();
  const qs = new URLSearchParams();
  if (filters?.event_id) qs.set('event_id', filters.event_id);
  if (filters?.status) qs.set('status', filters.status);
  if (filters?.rodeo_id) qs.set('rodeo_id', filters.rodeo_id);
  if (filters?.include_buddy_group) qs.set('include_buddy_group', 'true');
  const query = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: registrationOpsKeys.myRegistrations(filters),
    enabled: (options?.enabled ?? true) && isAuthenticated,
    queryFn: async () => {
      const res = await httpClient.get<{ data: Registration[] } | Registration[]>(
        `/registration-ops/my-registrations${query}`,
      );
      // API returns { data: [...], success: true } — unwrap to bare array
      return Array.isArray(res) ? res : (res as any)?.data ?? [];
    },
  });
}

export function useEventRegistrations(
  eventId: string,
  params?: PaginationParams,
) {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.pageSize) qs.set('limit', String(params.pageSize));
  const query = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: registrationOpsKeys.eventRegistrations(eventId, params),
    queryFn: () =>
      httpClient.get<{ data: Registration[]; total: number }>(
        `/registration-ops/event/${eventId}/registrations${query}`,
      ),
    enabled: !!eventId,
  });
}

export function useEventCapacity(eventId: string) {
  return useQuery({
    queryKey: registrationOpsKeys.eventCapacity(eventId),
    queryFn: async (): Promise<CapacityInfo> => {
      const raw = await httpClient.get<{
        max_contestants: number | null;
        confirmed_count: number;
        pending_count: number;
        available_spots: number | null;
        is_full: boolean;
      }>(`/registration-ops/event/${eventId}/capacity`);

      const registered = (raw.confirmed_count ?? 0) + (raw.pending_count ?? 0);
      const total = raw.max_contestants ?? 0;
      return {
        total_capacity: total,
        registered_count: registered,
        available_spots: raw.available_spots ?? Math.max(0, total - registered),
        waitlist_count: 0,
        is_full: !!raw.is_full,
      };
    },
    enabled: !!eventId,
  });
}

/**
 * Per-event registration policy: membership requirements, surcharges,
 * cash allowance, draw timing, etc. Lives under `registrationOpsKeys.all`
 * so registration mutations (incl. cancel) invalidate it automatically.
 */
export interface RegistrationPolicy {
  is_member: boolean;
  non_member_surcharge: number;
  allow_non_member_buddy_group: boolean;
  allow_cash_payment: boolean;
  /**
   * Whether Pay Later is offered for THIS event. Backend resolves
   * rodeos.allow_pay_later with events.allow_pay_later as a per-event
   * override. When false the Pay Later card must not render at all.
   * Optional on the type so a response from an older backend (or a
   * cached one) degrades to the historical "always on" behavior.
   */
  allow_pay_later?: boolean;
  /**
   * Whether membership is required for this rodeo — the org default with a
   * per-rodeo override, already resolved server-side. When false the
   * registration UI treats everyone as a member: no membership card, no
   * non-member surcharge, and the deferred payment options are open to all.
   */
  member_fee_enabled: boolean;
  membership_url: string | null;
  preference_config?: {
    ifPerformance: Array<'slack' | 'out' | 'performance'>;
    ifSlack: Array<'slack' | 'out' | 'performance'>;
  };
  entry_close_time?: string | null;
  fees_due_message?: string | null;
  /**
   * IANA timezone the rodeo's wall-clock fields are anchored to. Used by the
   * registration modal to format `entry_close_time` so every contestant sees
   * the same time the host entered, regardless of viewer locale.
   */
  timezone?: string | null;
  /**
   * Whether this is a finals rodeo — every contestant of the event runs in
   * EVERY performance the event's rounds are tied to, so buddy groups and
   * performance preferences don't apply. Optional so a response from an
   * older/cached backend degrades to the regular-rodeo behavior.
   */
  is_finals?: boolean;
  /**
   * The rodeo's own buddy-group switch, independent of `is_finals`. Both
   * must allow it for buddy groups to be offered.
   */
  buddy_groups_enabled?: boolean;
}

export function useRegistrationPolicy(eventId: string) {
  // Registration policy is auth-scoped — backend computes membership status,
  // surcharges, and buddy-group eligibility based on the calling user. For
  // visitors we skip the call entirely; the event detail page falls back to
  // showing base event info without member-specific calculations.
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    queryKey: registrationOpsKeys.registrationPolicy(eventId),
    enabled: !!eventId && isAuthenticated,
    queryFn: () =>
      httpClient.get<RegistrationPolicy>(
        `/registration-ops/registration-policy?event_id=${eventId}`,
      ),
  });
}

/**
 * Current user's unpaid balance across all events. Used by the registration
 * wizard to surface the "outstanding balance — pay before registering"
 * banner. Must refresh when registrations change so a cancel → re-register
 * flow doesn't show stale payment-pending state — hence the placement under
 * `registrationOpsKeys.all`.
 */
/**
 * One unpaid (past-due) registration entry. `registration_id` is what
 * the bulk-pay flow needs to settle these in a single PayPal capture;
 * `organization_name` is shown to the user in the per-org blocker
 * modal so they understand which org's balance they're being asked to
 * clear.
 */
export interface UnpaidEvent {
  registration_id?: string;
  event_id: string;
  event_name: string | null;
  organization_name?: string | null;
  amount_owed: number;
  /**
   * True when this row is collectible right now — i.e. the parent
   * rodeo's entry-close time has passed OR it carries a supplemental
   * balance from a rejected membership claim. False for pre-entry-close
   * pay-later seats: the user CAN settle early but isn't required to.
   *
   * The 402 registration-gate and the Funds page DUE NOW box only
   * count `is_due_now=true` rows. The header badge and the Funds page
   * TOTAL OWED line sum across the whole array. Optional in the type
   * for back-compat with older backend builds; consumers that depend
   * on the flag should default a missing value to `true` (safer for
   * an unknown row to show as due-now than to silently disappear from
   * the gate).
   */
  is_due_now?: boolean;
  /**
   * True when this row's linked payment is an ACH (bank) debit still actively
   * awaiting micro-deposit verification. It's part of `total_owed` (the
   * contestant DOES owe it) but must be excluded from the bulk "pay
   * outstanding balance" flow — paying it again by card supersedes the pending
   * verification. `amount_owed` carries the ACH price for these rows.
   */
  ach_in_flight?: boolean;
}

export interface OutstandingBalance {
  /**
   * Sum of every unpaid amount the user owes — pre-entry-close
   * pay-later seats, post-entry-close pending fees, and supplemental
   * rejection surcharges all included. The header-bar badge and the
   * Funds page TOTAL OWED line should both read this number.
   */
  total_owed: number;
  /**
   * Subset of `total_owed` that's enforceable right now (rows with
   * `is_due_now=true`). Drives the Funds page DUE NOW box and the
   * 402 registration-gate copy.
   */
  total_due_now: number;
  /**
   * Back-compat alias for `total_due_now`. New code should prefer
   * the explicit names above; this stays so older callers don't
   * break across the rollout.
   */
  total_outstanding: number;
  /**
   * Sum of `amount_owed` across rows flagged `ach_in_flight` — bank (ACH)
   * debits the contestant owes but is actively verifying. Part of `total_owed`
   * but EXCLUDED from anything the bulk "pay outstanding balance" flow can
   * settle. Drives the Funds-tab note explaining why the payable amount is
   * smaller than the total owed. Optional for back-compat with older builds.
   */
  total_ach_in_flight?: number;
  unpaid_events: UnpaidEvent[];
  /**
   * Manual ledger balance — admin-recorded charges (committee fronted
   * an entry fee, etc.) that haven't been paid off yet, minus any
   * unsettled credits on file. Surfaces alongside event-registration
   * debt as a separate Funds-tab section. Optional on the response so
   * old clients ignore it cleanly.
   */
  manual_charges?: ManualChargesBalance;
}

export interface ManualChargesUnsettledRow {
  id: string;
  type: 'charge' | 'refund' | 'adjustment' | 'comp' | 'credit';
  direction: 'debit' | 'credit';
  amount: number;
  currency: string;
  reason: string;
  notes: string | null;
  created_at: string;
  /** Display name (short_name preferred) of the org this entry was filed
   *  against; null for system-level / ALL-org entries. */
  organization_name: string | null;
}

/** A credit redemption — credit the contestant SPENT paying for an event at
 *  checkout. Rendered as a discrete "Credit applied → <event>" line in the
 *  Organization-ledger so the per-org balance reconciles with the rollup. */
export interface ManualChargesRedemptionRow {
  id: string;
  organization_id: string;
  organization_name: string | null;
  amount: number;
  created_at: string;
  event_name: string | null;
  rodeo_name: string | null;
}

/** A completed manual-charge payment, attributed to an org. Rendered as a
 *  "Payment" line so the ledger reconciles to the settled balance instead of
 *  the pre-payment amount. */
export interface ManualChargesPaymentRow {
  id: string;
  organization_name: string | null;
  amount: number;
  created_at: string;
}

export interface ManualChargesBalance {
  total_debits: number;
  total_credits: number;
  /** max(0, debits - credits) — what the user actually has to pay. */
  net_owed: number;
  /** Every adjustment id that contributes to this balance, in created_at ASC. */
  adjustment_ids: string[];
  unsettled: ManualChargesUnsettledRow[];
  /** Credit spent at event checkout (un-reversed). Already netted out of
   *  total_credits / net_owed above; surfaced as discrete lines so the per-org
   *  ledger shows WHERE the credit went. Optional for older API responses. */
  redemptions?: ManualChargesRedemptionRow[];
  /** Completed manual-charge payments, attributed per org. Shown as discrete
   *  "Payment" lines so the ledger reconciles to the settled balance. */
  payments?: ManualChargesPaymentRow[];
}

export function useOutstandingBalance(userId?: string) {
  // Per-user unpaid balance — meaningless for visitors; skip the call so
  // public pages don't fire a 401 on /payment-ops/outstanding-balance.
  // When `userId` is set, fetch THAT contestant's balance via the admin-gated
  // endpoint (admin User-Details → Financials reuses the same card).
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    queryKey: registrationOpsKeys.outstandingBalance(userId),
    enabled: isAuthenticated,
    queryFn: () =>
      httpClient.get<OutstandingBalance>(
        userId
          ? `/payment-ops/admin/users/${userId}/outstanding-balance`
          : '/payment-ops/outstanding-balance',
      ),
  });
}

/**
 * Authoritative per-event registration block. Backend resolves the
 * event's organization (via primary_organization_id with a name-based
 * fallback) and returns the user's past-due unpaid registrations for
 * that org. Drives the preflight modal on "Register Now" click — when
 * `blocked` is true, the wizard is suppressed and the bulk-pay flow
 * for these registrations is offered instead.
 */
export interface EventRegistrationBlock {
  blocked: boolean;
  organization_id: string | null;
  organization_name: string | null;
  unpaid_events: UnpaidEvent[];
  /**
   * Org-scoped manual-charge debt (admin-recorded fronted fees, etc.)
   * that also blocks new registrations against this org. Flat
   * committee charges have no entry-close grace window — if it's
   * unsettled and tied to this org, it blocks.
   */
  manual_charges?: {
    total_debits: number;
    total_credits: number;
    net_owed: number;
    adjustment_ids: string[];
    /** Net of the BLOCKING charges only — this is what actually gates
     *  registration and what the pay-to-unblock flow should settle. */
    blocking_net_owed: number;
    blocking_adjustment_ids: string[];
    /** The blocking charges with their admin-entered reason, so the gate
     *  dialog can show why each fee is owed. */
    blocking_charges: Array<{ id: string; amount: number; reason: string | null }>;
  };
}

export function useEventRegistrationBlock(eventId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: [...registrationOpsKeys.all, 'event-registration-block', eventId] as const,
    queryFn: () =>
      httpClient.get<EventRegistrationBlock>(
        `/payment-ops/event-registration-block?event_id=${eventId}`,
      ),
    enabled: enabled && !!eventId,
  });
}

export function usePartnerNotifications() {
  return useQuery({
    queryKey: registrationOpsKeys.partnerNotifications(),
    queryFn: () =>
      httpClient.get<PartnerNotification[]>(
        '/registration-ops/partner/notifications',
      ),
  });
}

/**
 * Pre-flight: which members of `buddyGroupId` are already registered for
 * `eventId`. Drives the on-behalf member picker so the primary sees an
 * "(already registered)" indicator before submitting. The endpoint also
 * opportunistically backfills any unaffiliated solo registrations into
 * the group, so we invalidate this query whenever a registration mutation
 * runs (handled by the shared `registrationOpsKeys.all` invalidation in
 * the create/cancel hooks below).
 */
export interface BuddyGroupRegistrationStatus {
  registered_user_profile_ids: string[];
  /**
   * Per-member active registration counts. Members with zero rows are
   * omitted, so use `?? 0` when reading. Drives the multi-reg cap gate
   * on the on-behalf member picker.
   */
  registration_counts: Record<string, number>;
  /**
   * Per-member list of positions already filled by an active row.
   * Empty string represents a solo / null-position row; team roping
   * rows surface "Header"/"Heeler" verbatim. Members with zero rows
   * are omitted. Drives the position picker on the on-behalf path so
   * the primary can see which slots are still open for the teammate
   * they're registering.
   */
  registered_positions_by_user: Record<string, string[]>;
  /**
   * Per-member, per-position active registration counts. Outer key
   * is user_profile_id; inner key is the position ("Header",
   * "Heeler", or "" for solo). Missing inner keys mean zero. Used
   * by the on-behalf picker to enforce the per-position cap on team
   * roping multi-reg events (max=2 means up to 2 Headers AND 2
   * Heelers per contestant).
   */
  position_counts_by_user: Record<string, Record<string, number>>;
}

export function useBuddyGroupRegistrationStatus(
  eventId: string | undefined,
  buddyGroupId: string | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: registrationOpsKeys.buddyGroupStatus(
      eventId ?? '',
      buddyGroupId ?? '',
    ),
    queryFn: async (): Promise<BuddyGroupRegistrationStatus> => {
      const res = await httpClient.get<{
        registered_user_profile_ids: string[];
        registration_counts?: Record<string, number>;
        registered_positions_by_user?: Record<string, string[]>;
        position_counts_by_user?: Record<string, Record<string, number>>;
        success: boolean;
      }>(
        `/registration-ops/event/${eventId}/buddy-group-status?buddy_group_id=${buddyGroupId}`,
      );
      return {
        registered_user_profile_ids: res.registered_user_profile_ids ?? [],
        // Older backend builds returned only the ID list; fall back to a
        // synthesized `id → 1` map so consumers always see a count of at
        // least 1 for anyone in the legacy list. New builds return the
        // real per-user counts.
        registration_counts:
          res.registration_counts ??
          Object.fromEntries(
            (res.registered_user_profile_ids ?? []).map((id) => [id, 1]),
          ),
        // Older backends omit per-position info; fall back to an empty
        // map so callers can use `?? []` and still render. New builds
        // return real per-user position lists for the on-behalf picker.
        registered_positions_by_user: res.registered_positions_by_user ?? {},
        // Older backends omit per-position counts. Synthesize from the
        // legacy `registered_positions_by_user` when present (each known
        // position counted as 1), so consumers can still distinguish
        // "filled" from "not filled" even if exact counts are off.
        position_counts_by_user:
          res.position_counts_by_user ??
          Object.fromEntries(
            Object.entries(res.registered_positions_by_user ?? {}).map(
              ([id, positions]) => [
                id,
                Object.fromEntries(positions.map((p) => [p, 1])),
              ],
            ),
          ),
      };
    },
    enabled: enabled && !!eventId && !!buddyGroupId,
  });
}

export function useHandicapCheck(eventId: string, position: string) {
  return useQuery({
    queryKey: registrationOpsKeys.handicapCheck(eventId, position),
    queryFn: async () => {
      const res = await httpClient.get<HandicapCheckResult & { success: boolean }>(
        `/registration-ops/handicap-check?event_id=${eventId}&position=${position}`,
      );
      return res as HandicapCheckResult;
    },
    enabled: !!eventId && !!position,
  });
}

// --- Mutation Hooks ---
export function useCreateRegistration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RegisterData) =>
      httpClient.post<Registration>('/registration-ops/register', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      // New entry must show up on the dashboard's "Upcoming Events"
      // list immediately — the dashboard query lives under a separate
      // key tree, so registration-ops invalidation alone left it stale
      // until a manual refresh.
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
    },
  });
}

/**
 * Admin manual entry: register a contestant on their behalf. The
 * registration row's user_profile_id is the supplied target, and
 * created_by/updated_by are the admin actor — that pair is the audit
 * trail distinguishing admin entries from self-entries without
 * requiring a new column.
 *
 * Bypasses the buddy-group on-behalf gate (admin authority replaces
 * it). All other registration rules (capacity, ruleset eligibility,
 * partner caps, fees) still apply via the shared service.
 *
 * Defaults `payment_method` to `pay_later` server-side when omitted so
 * a committee can hold a seat for a contestant who'll settle later.
 */
export interface AdminCreateRegistrationData {
  target_user_profile_id: string;
  position?: 'Header' | 'Heeler';
  partner_user_profile_id?: string | null;
  paid_for_partner?: boolean;
  payment_method?: 'pay_later' | 'cash' | 'stripe' | string | null;
  manual_handicap_value?: number | null;
  buddy_group_id?: string;
  buddy_group_position?: number;
  first_performance_preference?: string;
  second_performance_preference?: string;
  /**
   * Admin-only escape hatch for team roping: hold a partnerless seat that
   * Draw Management can later pair with another unmatched individual.
   * When true, the row is persisted as registration_type='team_primary',
   * partner_status='needs_partner', draw_eligible=false so it surfaces in
   * the existing partnerless picker on the Draw Management page. Ignored
   * on solo events. Bypasses the partner-required guard server-side.
   */
  as_individual_unmatched?: boolean;
}

export function useAdminCreateRegistration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      ...data
    }: AdminCreateRegistrationData & { eventId: string }) =>
      httpClient.post<Registration>(
        `/registration-ops/admin/events/${eventId}/register`,
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      // The admin event-registrations table reads from registration-ops,
      // and the contestant's own dashboard view should pick up the new
      // entry on their next visit — but the admin's dashboard doesn't
      // own the row, so dashboard-data invalidation isn't required.
    },
  });
}

export function useRespondToPartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      registrationId,
      ...data
    }: PartnerRespondData & { registrationId: string }) =>
      httpClient.post<Registration>(
        `/registration-ops/partner/${registrationId}/respond`,
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      // Accepting a partner invitation makes the team-roping entry
      // become a confirmed seat for the partner, which should surface
      // on their dashboard immediately. Decline also flips the primary's
      // row to needs_partner, which the dashboard renders differently.
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
    },
  });
}

interface ReinvitePartnerData {
  partner_user_profile_id: string;
}

interface ReinvitePartnerResponse {
  success: boolean;
  registration: Registration;
  partner_registration: Registration;
  partner_notification: unknown;
  action: 'reinvited';
}

/**
 * Send a fresh partner invitation on a team-roping entry that's stuck in
 * the `needs_partner` state (the previous partner declined). Skips the
 * cancel-and-re-register loop so the user keeps their already-paid
 * transaction fee. The backend rejects self-invites and rejects partners
 * who already have an active row for the same event.
 */
export function useReinvitePartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      registrationId,
      ...data
    }: ReinvitePartnerData & { registrationId: string }) =>
      httpClient.post<ReinvitePartnerResponse>(
        `/registration-ops/partner/${registrationId}/reinvite`,
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      // Dashboard shows the needs_partner warning + CTA, so refresh it
      // immediately when the invite goes out.
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
    },
  });
}

interface AdminPairPartnerlessData {
  primary_registration_id: string;
  partner_registration_id: string;
}

interface AdminPairPartnerlessResponse {
  success: boolean;
  primary_registration: Registration;
  partner_registration: Registration;
  action: 'paired';
}

/**
 * Admin-only. Pair two existing partnerless team-roping registrations on
 * the same event into one team. Used from the delegate Draws panel when
 * the admin sees two orphan rows of opposite positions (Header + Heeler)
 * and wants to link them up directly instead of asking the contestants
 * to re-invite each other. Backend validates same event + team-roping +
 * partnerless + opposite-position before flipping the back-pointers.
 */
export function useAdminPairPartnerless() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminPairPartnerlessData) =>
      httpClient.post<AdminPairPartnerlessResponse>(
        '/registration-ops/admin/partner/pair-existing',
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      // The pair flips draw_eligible=true on both rows so the draft draw
      // recompute needs to see them — invalidate the draw-ops cache too.
      qc.invalidateQueries({ queryKey: ['draw-ops'] });
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
    },
  });
}

interface AdminPendingTeamActionData {
  registration_id: string;
}

interface AdminCancelPendingResponse {
  success: boolean;
  action: 'cancelled';
  primary_registration_id: string;
  partner_registration_id: string;
  event_id: string;
}

/**
 * Admin-only. Cancel a pending team-roping partner invitation entirely.
 * The partner row is cancelled (their seat goes away) and the inviter is
 * demoted to needs_partner so they remain in the event but cannot draw
 * until they invite someone else. Both parties are notified by email +
 * in-app notification. Used from the Draw Management Pending Approval
 * Teams panel.
 */
export function useAdminCancelPendingPartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminPendingTeamActionData) =>
      httpClient.post<AdminCancelPendingResponse>(
        '/registration-ops/admin/partner/cancel-pending',
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      qc.invalidateQueries({ queryKey: ['draw-ops'] });
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
    },
  });
}

interface AdminAcceptOnBehalfResponse {
  success: boolean;
  action: 'accepted';
  primary_registration_id: string;
  partner_registration_id: string;
  event_id: string;
}

/**
 * Admin-only. Accept a pending team-roping partner invitation on behalf
 * of the invitee. Flips both rows to partner_status=approved, recomputes
 * status_id (pending_confirmation → confirmed, pending_confirmation_and_
 * payment → pending_payment), fires the inviter "partner confirmed" and
 * the invitee "you're entered" notifications. Used when the contestant
 * agreed verbally but hasn't clicked accept.
 */
export function useAdminAcceptOnBehalfPartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminPendingTeamActionData) =>
      httpClient.post<AdminAcceptOnBehalfResponse>(
        '/registration-ops/admin/partner/accept-on-behalf',
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      qc.invalidateQueries({ queryKey: ['draw-ops'] });
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
    },
  });
}

export interface AdminReassignPartnerData {
  keep_registration_id: string;
  // Either reuse-existing (set new_partner_registration_id)
  new_partner_registration_id?: string;
  // OR create-new-entry (set the next three together)
  new_partner_user_profile_id?: string;
  new_partner_position?: 'Header' | 'Heeler';
  fee_mode?: 'normal' | 'free';
}

interface AdminReassignPartnerResponse {
  success: boolean;
  action: 'reassigned';
  kept_registration_id: string;
  dropped_registration_id: string;
  new_partner_registration_id: string;
  new_partner_user_profile_id: string;
  created_new_entry: boolean;
  displaced_from_pending_team: {
    primary_id: string;
    partner_id: string;
  } | null;
  event_id: string;
}

/**
 * Admin-only. Reassign one side of a pending team to a new partner. The
 * "keep" side stays in the team; the other side of the original pending
 * team is dropped (cancelled if it was the team_partner, demoted to
 * needs_partner if it was the team_primary). The new partner comes from
 * one of three buckets:
 *   - Unassigned (truly partnerless / needs_partner)  → pass
 *     `new_partner_registration_id`
 *   - In another pending team                         → pass
 *     `new_partner_registration_id`; backend breaks that pending team too
 *   - In an approved team (multi-entry)               → pass
 *     `new_partner_user_profile_id` + `new_partner_position` + `fee_mode`;
 *     backend creates a SECOND event_registrations row for them at $0
 *     (fee_mode='free') or full entry_fee (fee_mode='normal')
 */
export function useAdminReassignPartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminReassignPartnerData) =>
      httpClient.post<AdminReassignPartnerResponse>(
        '/registration-ops/admin/partner/reassign',
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      qc.invalidateQueries({ queryKey: ['draw-ops'] });
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
    },
  });
}

interface AdminSendReminderResponse {
  success: boolean;
  action: 'reminder_sent';
  partner_registration_id: string;
  partner_reminder_last_sent_at: string;
  event_id: string;
}

/**
 * Admin-only. Nudge the invitee of a still-pending partner invitation
 * to accept or decline before the draw runs. Stamps
 * partner_reminder_last_sent_at on the team_partner row so the UI can
 * show "last reminded Xh ago".
 */
export function useAdminSendPartnerReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminPendingTeamActionData) =>
      httpClient.post<AdminSendReminderResponse>(
        '/registration-ops/admin/partner/send-reminder',
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      qc.invalidateQueries({ queryKey: ['draw-ops'] });
    },
  });
}

export interface AdminSendCustomMessageData {
  registration_id: string;
  subject: string;
  body: string;
}

interface AdminSendCustomMessageResponse {
  success: boolean;
  action: 'message_sent';
  registration_id: string;
  admin_message_last_sent_at: string;
  event_id: string;
}

/**
 * Admin-only. Send a one-off freeform email + in-app notification to
 * the contestant who owns a given registration. Used from the
 * partnerless-row "Send message" modal — common use case is "Hey, did
 * you have a partner in mind?" while their entry has no partner yet.
 * Email goes FROM support@highcallrodeo.com so replies reach a
 * monitored inbox. Stamps admin_message_last_sent_at so the UI can
 * show "last messaged Xh ago".
 */
export function useAdminSendCustomMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminSendCustomMessageData) =>
      httpClient.post<AdminSendCustomMessageResponse>(
        '/registration-ops/admin/registration/send-message',
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      qc.invalidateQueries({ queryKey: ['draw-ops'] });
    },
  });
}

/**
 * Per-registration refund summary returned by /cancel-team. The backend
 * issues a partial Stripe refund (entry fee + non-member surcharge,
 * retaining the per-event transaction fee) when the registration has a
 * completed Stripe capture. Cash / unpaid registrations come back with
 * `refunded: false` and a `reason` like `not_stripe_capture` or
 * `no_payment_linked`.
 */
export interface CancellationRefundInfo {
  registration_id: string;
  refunded: boolean;
  reason?: string;
  amount_refunded?: number;
  transaction_fee_retained?: number;
  currency?: string;
  provider_refund_id?: string;
  provider_status?: string;
  error?: string;
  breakdown?: {
    entry_fee: number;
    non_member_fee: number;
    transaction_fee_retained: number;
    paid_total: number;
  };
}

export interface CancelTeamRegistrationResponse {
  success: boolean;
  cancelled_registrations: number;
  cancelled_registration_ids: string[];
  /**
   * True when the canceller was a `team_partner` and only their own
   * row was cancelled — the team primary's registration + payment
   * survived and got converted back to an `individual` entry. Lets
   * the UI surface a different success message ("Your spot stays;
   * the primary keeps their registration") instead of the standard
   * "team cancelled" copy.
   */
  partner_left_standing?: boolean;
  refunds?: CancellationRefundInfo[];
}

export function useCancelTeamRegistration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (registrationId: string) =>
      httpClient.delete<CancelTeamRegistrationResponse>(
        `/registration-ops/${registrationId}/cancel-team`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      // Cancellation can change payment status (refunded) — refresh
      // payment-ops queries so any open history view picks up the change.
      qc.invalidateQueries({ queryKey: ['payment-ops'] });
      // Refresh dashboard so any cancelled rows drop out of "Upcoming"
      // immediately without forcing the user to reload.
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
    },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) =>
      httpClient.post(
        `/registration-ops/partner/notifications/${notificationId}/read`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: registrationOpsKeys.partnerNotifications(),
      });
    },
  });
}

// --- Payment-integrated registration hooks ---

/**
 * Register with Stripe payment. Returns the Stripe client_secret +
 * PaymentIntent id so the frontend can mount Stripe Elements for
 * confirmation. The webhook is the source of truth for settlement.
 */
export function useRegisterWithPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RegisterWithPaymentData) =>
      httpClient.post<PaymentRegistrationResponse>(
        '/registration-payment-ops/register',
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      // Stripe registration creates the row up-front (status
      // pending_payment); it should appear on the dashboard right
      // away with a Pay Now CTA, not after the next refresh.
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
    },
  });
}

/**
 * Register with cash payment (pay at event).
 */
export function useRegisterWithCash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RegisterWithCashData) =>
      httpClient.post<CashRegistrationResponse>(
        '/cash-payment-ops/register',
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      // Cash registrations are confirmed immediately, so the new
      // entry should show up on "Upcoming Events" without a refresh.
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
    },
  });
}

/**
 * Verify a Stripe PaymentIntent succeeded after Stripe Elements confirms
 * the payment. The webhook is still the canonical settle path; this is the
 * optimistic fast path so the UI updates immediately.
 *
 * Invalidates registration-ops AND dashboard-data — without the latter,
 * the dashboard's "Upcoming Events" Pay Now button stays visible after a
 * successful capture until the user manually refreshes.
 */
export function useCompleteRegistrationPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ registrationId, payment_intent_id }: CompletePaymentData) =>
      httpClient.post<{ success: boolean; registration_id: string; status: string; payment_status: string }>(
        `/registration-payment-ops/complete/${registrationId}`,
        { payment_intent_id },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
      qc.invalidateQueries({ queryKey: ['payment-ops'] });
    },
  });
}

/** Response shape from /sync-payment-method. */
export interface SyncPaymentMethodResult {
  payment_intent_id: string;
  total_amount: number;
  transaction_fee_total: number;
  payment_method_type: 'card' | 'us_bank_account';
}

/**
 * Re-price an in-flight Stripe PaymentIntent for the method the user just
 * picked in <PaymentElement />. ACH (us_bank_account) drops the event's
 * transaction fee per the business rule; card restores the full total.
 *
 * Caller MUST also call `elements.fetchUpdates()` after this resolves so
 * Stripe Elements reflects the new amount before the user confirms.
 */
export function useSyncPaymentMethod() {
  return useMutation({
    mutationFn: (input: {
      payment_intent_id: string;
      payment_method_type: 'card' | 'us_bank_account';
    }) =>
      httpClient.post<SyncPaymentMethodResult>(
        '/registration-payment-ops/sync-payment-method',
        input,
      ),
  });
}

/**
 * Cancel a pending Stripe registration.
 * Called when the user dismisses the Stripe checkout dialog without
 * completing payment. Passes issue_refund: false because no capture has
 * occurred yet.
 */
export function useCancelRegistrationPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (registrationId: string) =>
      httpClient.post<{ success: boolean; registration_id: string }>(
        `/registration-payment-ops/cancel/${registrationId}`,
        { issue_refund: false },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
    },
  });
}

/**
 * Fully purge a pending_payment registration that was created during a
 * Stripe flow but never paid. Distinct from `useCancelRegistrationPayment`
 * — abandon DELETES the row, leaving no trace in the user's funds list,
 * whereas cancel marks it `cancelled` for audit. Used by
 * RegistrationModal when the user dismisses the dialog mid-checkout.
 *
 * The backend gates this on status=pending_payment + payment != completed,
 * so it's safe to call optimistically — it'll 400 if money has already
 * moved.
 */
export function useAbandonRegistrationPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (registrationId: string) =>
      httpClient.post<{ success: boolean; registration_id: string; abandoned: true }>(
        `/registration-payment-ops/abandon/${registrationId}`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
    },
  });
}

/**
 * Mint a fresh Stripe PaymentIntent for an existing pending_payment
 * registration. Drives the "Pay Now" button on registrations originally
 * created with payment_method='pay_later' or 'cash'. The caller wires the
 * returned `client_secret` into Stripe Elements; on confirmation it should
 * call `useCompleteRegistrationPayment` with the same registrationId +
 * payment_intent_id.
 */
export function useCreatePayNowOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (registrationId: string) =>
      httpClient.post<PaymentRegistrationResponse>(
        `/registration-payment-ops/pay-now/${registrationId}`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
    },
  });
}

/** Response shape from /pay-now-bulk + /pay-now-bulk-deferred. */
export interface BulkPayOrderResponse {
  payment_id: string;
  /** Stripe PaymentIntent id covering every registration in the batch.
   *  Omitted on the deferred-mint path (no PI yet). */
  stripe_payment_intent_id?: string;
  /** Stripe client_secret for the bulk PaymentIntent. Null on the
   *  deferred-mint path. */
  client_secret: string | null;
  /** Stripe publishable key for the active env. */
  stripe_publishable_key: string;
  total_amount: number;
  registration_count: number;
  payment_deadline: string;
  covered_registration_ids: string[];
  /** Number of manual-charge adjustments rolled into this capture; only
   *  present when the caller passed `manual_charge_adjustment_ids`. */
  manual_charge_count?: number;
  /** Manual-charge net dollar amount included in `total_amount`. */
  manual_charge_amount?: number;
  /**
   * Deferred-mint only. Totals quoted for BOTH payment methods so the
   * checkout dialog can show the user the card-vs-ACH price difference.
   */
  totals_by_method?: {
    card: { total_amount: number; transaction_fee_total: number };
    us_bank_account: { total_amount: number; transaction_fee_total: number };
  };
  /**
   * Deferred-mint only. Per-org credit the user may apply to THIS basket. Org
   * credit is org-scoped, so each org's `applicable_credit` is capped to that
   * org's portion of the basket — org-A credit can never pay org-B's fees.
   * `total_credit` is the org's full balance (shown as "total"); the dialog
   * uses `applicable_credit` as the per-org apply ceiling. One entry per
   * basket org with usable credit; empty/absent when none.
   */
  credit_options?: CreditOption[];
  success: boolean;
}

/** Per-org credit affordance for a bulk basket (see BulkPayOrderResponse). */
export interface CreditOption {
  organization_id: string;
  organization_name: string | null;
  total_credit: number;
  applicable_credit: number;
}

/** One org's credit contribution to a split-tender / credit-only settlement. */
export interface CreditApplicationInput {
  organization_id: string;
  amount: number;
}

interface BulkPayOrderInput {
  registration_ids: string[];
  return_url?: string;
  cancel_url?: string;
  /**
   * Optional unsettled `account_adjustments` ids to roll into the same
   * Stripe charge. Lets the Funds-tab "Pay outstanding balance" button
   * settle event-registration debt + admin-recorded manual charges in
   * one capture. Server validates ownership + unsettled state and
   * silently skips ids that don't qualify.
   */
  manual_charge_adjustment_ids?: string[];
}

/**
 * Update the row owner's performance preferences AFTER a registration
 * was created. Backend rejects with 409 BUDDY_GROUP_LOCKED on rows
 * attached to a buddy group (those are locked at the group level and
 * have to be changed via the buddy-group setup page). Also rejects with
 * 409 ENTRY_CLOSE_PASSED once the rodeo's entry-close cutoff has passed.
 */
export interface UpdateRegistrationPreferencesInput {
  registrationId: string;
  first_performance_preference: string | null;
  second_performance_preference: string | null;
}

export function useUpdateRegistrationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      registrationId,
      first_performance_preference,
      second_performance_preference,
    }: UpdateRegistrationPreferencesInput) =>
      httpClient.put<{
        success: boolean;
        registration: {
          id: string;
          first_performance_preference: string | null;
          second_performance_preference: string | null;
          resolved_performance_slot: string | null;
        };
      }>(`/registration-ops/${registrationId}/preferences`, {
        first_performance_preference,
        second_performance_preference,
      }),
    onSuccess: () => {
      // Refresh both the dashboard upcoming list and the per-event
      // registration cache so the new picks (and the cleared resolved
      // slot) show up immediately.
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
    },
  });
}

/**
 * Mint ONE Stripe PaymentIntent covering many pending_payment
 * registrations. The "Pay outstanding balance" CTA on the funds tab uses
 * this so the user settles their entire account in a single Stripe
 * checkout instead of being walked through N separate confirmations.
 * Cuts processor fees too.
 */
export function useCreateBulkPayOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkPayOrderInput) =>
      httpClient.post<BulkPayOrderResponse>(
        '/registration-payment-ops/pay-now-bulk',
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
    },
  });
}

// ─── Deferred-mint flow hooks ───────────────────────────────────────────
//
// Companion hooks for the deferred-mint Stripe checkout flow. Instead of
// minting a Stripe PaymentIntent at registration time (which leaves
// orphan PIs in Stripe when users open and bail on the checkout dialog),
// the deferred flow creates only local state at registration time and
// mints the Stripe PI at submit time when the user's chosen payment
// method is known.
//
// Net effect: zero Stripe-side records for users who open and bail.
// Also fixes the ACH-fee bug — the captured amount is computed for the
// chosen method at mint time (ACH waives the transaction fee).
//
// Wire the deferred dialog as:
//   const register = useRegisterWithPaymentDeferred();
//   const mint = useMintIntent();
//   ...
//   <StripeCheckoutDialog
//     quoteOrder={() => register.mutateAsync(data)}
//     confirmIntent={(payment_id, payment_method_type) =>
//       mint.mutateAsync({ payment_id, payment_method_type })
//     }
//     ...
//   />
// ────────────────────────────────────────────────────────────────────────

/**
 * Deferred-mint twin of useRegisterWithPayment. Creates the registration
 * + local payment row but does NOT mint a Stripe PI. Returns
 * totals_by_method so the dialog can render the card-vs-ACH price.
 */
export function useRegisterWithPaymentDeferred() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RegisterWithPaymentData) =>
      httpClient.post<PaymentRegistrationResponse>(
        '/registration-payment-ops/register-deferred',
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
    },
  });
}

/**
 * Deferred-mint twin of useCreatePayNowOrder.
 */
export function useCreatePayNowOrderDeferred() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (registrationId: string) =>
      httpClient.post<PaymentRegistrationResponse>(
        `/registration-payment-ops/pay-now-deferred/${registrationId}`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
    },
  });
}

/**
 * Deferred-mint twin of useCreateBulkPayOrder.
 */
export function useCreateBulkPayOrderDeferred() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkPayOrderInput) =>
      httpClient.post<BulkPayOrderResponse>(
        '/registration-payment-ops/pay-now-bulk-deferred',
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
    },
  });
}

/**
 * Mint a Stripe PaymentIntent for an existing deferred payment row.
 * Called by the dialog at submit time. Returns client_secret which the
 * dialog hands to stripe.confirmPayment.
 */
export function useMintIntent() {
  return useMutation({
    mutationFn: (input: {
      payment_id: string;
      payment_method_type: 'card' | 'us_bank_account';
      /** Split tender (single-org legacy): org credit to apply, reducing the
       *  Stripe charge. */
      credit_amount?: number;
      /** Multi-org split tender (bulk baskets): per-org credit contributions,
       *  each capped server-side to that org's portion of the basket. */
      credit_applications?: CreditApplicationInput[];
    }) =>
      httpClient.post<MintIntentResponse>(
        '/registration-payment-ops/mint-intent',
        input,
      ),
  });
}

/** Response shape from POST /settle-with-credit. */
export interface SettleWithCreditResponse {
  payment_id: string;
  credit_applied: number;
  confirmed_registration_ids: string[];
  failed_registration_ids: string[];
  success: boolean;
}

/**
 * Settle a deferred BULK payment ENTIRELY with org account credit — no Stripe
 * charge. Used when the user's per-org credit covers 100% of the basket. Each
 * org's credit is capped server-side to that org's portion of the basket.
 * Invalidates the same caches as a completed bulk payment so balances refresh.
 */
export function useSettleWithCredit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      payment_id: string;
      credit_applications: CreditApplicationInput[];
    }) =>
      httpClient.post<SettleWithCreditResponse>(
        '/registration-payment-ops/settle-with-credit',
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
      qc.invalidateQueries({ queryKey: ['payment-ops'] });
    },
  });
}

/**
 * How much org account credit the user can apply toward a registration's
 * payment (split tender). Resolves the registration's org server-side and
 * returns the available balance for it — org-scoped. Drives the Pay Now
 * dialog's "apply credit" control.
 */
export function useRegistrationCredit(
  registrationId: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: [...registrationOpsKeys.all, 'registration-credit', registrationId] as const,
    queryFn: () =>
      httpClient.get<{
        organization_id: string | null;
        organization_name: string | null;
        available: number;
      }>(`/payment-ops/registration-credit/${registrationId}`),
    enabled: enabled && !!registrationId,
    staleTime: 15 * 1000,
  });
}

/**
 * Available org credit for ENTERING an event (new-registration flow, before a
 * registration row exists). Resolves the event's org server-side. Org-scoped.
 */
export function useEventCredit(
  eventId: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: [...registrationOpsKeys.all, 'event-credit', eventId] as const,
    queryFn: () =>
      httpClient.get<{
        organization_id: string | null;
        organization_name: string | null;
        available: number;
      }>(`/payment-ops/event-credit/${eventId}`),
    enabled: enabled && !!eventId,
    staleTime: 15 * 1000,
  });
}

/**
 * Cancel a PaymentIntent that was speculatively minted on the Pay click but
 * never confirmed (user backed out of the Apple Pay sheet / form). Cancels
 * the upstream Stripe PI + local payment row; never touches the registration.
 * Best-effort — a missing row or already-cancelled PI is a successful no-op.
 */
export function useAbandonIntent() {
  return useMutation({
    mutationFn: (payment_id: string) =>
      httpClient.post<{ success: boolean }>(
        '/registration-payment-ops/abandon-intent',
        { payment_id },
      ),
  });
}

/** Response shape from /complete-bulk. */
export interface BulkCompletionResponse {
  payment_id: string;
  captured_amount: number;
  confirmed_registration_ids: string[];
  failed_registration_ids: string[];
  success: boolean;
}

/**
 * Verify a bulk Stripe PaymentIntent succeeded after Stripe Elements
 * confirms it. Confirms every registration the intent covered in one
 * call. Returns per-row confirmation results so the UI can show "5 of 6
 * confirmed — refresh and retry the missing one" if a single
 * registration's confirmation throws.
 */
export function useCompleteBulkPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payment_intent_id: string) =>
      httpClient.post<BulkCompletionResponse>(
        '/registration-payment-ops/complete-bulk',
        { payment_intent_id },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
      qc.invalidateQueries({ queryKey: ['payment-ops'] });
    },
  });
}
